/**
 * Public embed page for WP iframe: /embed/configurator?token=xxx
 * Renders ConfiguratorForm; submit POSTs to /api/public/configurator with token.
 * Shows a result popup after submission (success, atypical, custom_required, error).
 */

import { useState, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ConfiguratorLayout from "@/components/price-generator/ConfiguratorLayout";
import type { ConfiguratorFormValues, ConfiguratorProductOption } from "@/components/price-generator/ConfiguratorForm";
import { apiUrl } from "@/lib/api-client";
import { reportConfiguratorConversion } from "@/lib/google-ads-conversion";

type ResultType = "Základná" | "Atypická" | "custom_required";

export interface QuoteSummary {
  email: string;
  width: number;
  height: number;
  surface?: string;
  color?: string;
  motiv?: string;
  electricDrive?: boolean;
}

interface SubmitResult {
  success: boolean;
  dealId?: string;
  resultType?: ResultType;
  error?: string;
  noMatchReason?: "no_match" | "size_too_small" | "size_too_large";
  /** Filled on success from submitted form values for the result summary. */
  quoteSummary?: QuoteSummary;
}

function CheckCircleIcon() {
  return (
    <svg className="w-16 h-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
    </svg>
  );
}

function InfoCircleIcon() {
  return (
    <svg className="w-16 h-16 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0zm-9-3.75h.008v.008H12V8.25z" />
    </svg>
  );
}

function ErrorCircleIcon() {
  return (
    <svg className="w-16 h-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0zm-9 3.75h.008v.008H12v-.008z" />
    </svg>
  );
}

function ResultScreen({ result, onReset }: { result: SubmitResult; onReset: () => void }) {
  const { t } = useTranslation();

  if (!result.success) {
    return (
      <div className="configurator-layout flex flex-col items-center justify-center min-h-[100dvh] px-6 text-center bg-background">
        <ErrorCircleIcon />
        <h2 className="configurator-title mt-6 !text-2xl">{t("configurator.resultErrorTitle")}</h2>
        <p className="mt-3 text-sm text-muted-foreground max-w-md">{result.error}</p>
        <button
          type="button"
          onClick={onReset}
          className="configurator-btn-primary mt-8 px-8 py-3 text-sm font-semibold rounded-md"
        >
          {t("configurator.resultNewQuote")}
        </button>
      </div>
    );
  }

  const isCustom = result.resultType === "custom_required";
  const isAtypical = result.resultType === "Atypická";
  const noMatchReason = result.noMatchReason;
  const isSpecialSize = noMatchReason === "size_too_small" || noMatchReason === "size_too_large" || noMatchReason === "no_match";

  const title = isSpecialSize
    ? t("configurator.resultTitleSpecial")
    : isCustom
      ? t("configurator.resultTitleCustom")
      : t("configurator.resultTitle");

  let message: string;
  if (isSpecialSize) {
    message = t("configurator.resultSpecialProject");
  } else if (isAtypical) {
    message = t("configurator.resultSuccessAtypical");
  } else {
    message = t("configurator.resultSuccessStandard");
  }

  const summary = result.quoteSummary;

  return (
    <div className="configurator-layout flex flex-col items-center justify-center min-h-[100dvh] px-6 text-center bg-background">
      {isSpecialSize ? <InfoCircleIcon /> : <CheckCircleIcon />}
      <h2 className="configurator-title mt-6 !text-2xl">{title}</h2>
      <p className="mt-3 text-[15px] leading-relaxed text-foreground/80 max-w-md">{message}</p>
      {summary && (
        <div className="mt-4 w-full max-w-sm text-left rounded-lg border border-border bg-muted/30 px-4 py-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">{t("configurator.resultSummaryTitle")}</p>
          <ul className="space-y-1.5 text-sm text-foreground">
            <li><span className="text-muted-foreground">{t("configurator.resultSentTo")}</span> {summary.email}</li>
            <li><span className="text-muted-foreground">{t("configurator.resultSize")}</span> {summary.width} × {summary.height} mm</li>
            {summary.surface && <li><span className="text-muted-foreground">{t("configurator.surfaceShort")}</span> {summary.surface}</li>}
            {summary.color && <li><span className="text-muted-foreground">{t("configurator.colorShort")}</span> {summary.color}</li>}
            {summary.motiv && <li><span className="text-muted-foreground">{t("configurator.gateMotif")}</span> {summary.motiv === "m" || summary.motiv === "1" ? "Prelis M" : summary.motiv === "l" || summary.motiv === "2" ? "Prelis L" : summary.motiv}</li>}
            <li><span className="text-muted-foreground">{t("configurator.electricDrive")}</span> {summary.electricDrive ? t("configurator.resultYes") : t("configurator.resultNo")}</li>
          </ul>
        </div>
      )}
      <p className="mt-4 text-xs text-muted-foreground">
        {t("configurator.disclaimer")}
      </p>
      <button
        type="button"
        onClick={onReset}
        className="configurator-btn-primary mt-8 px-8 py-3 text-sm font-semibold rounded-md"
      >
        {t("configurator.resultNewQuote")}
      </button>
    </div>
  );
}

export default function EmbedConfigurator() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const token = searchParams.get("token")?.trim();
  const [options, setOptions] = useState<ConfiguratorProductOption[]>([]);
  const [priceCombinations, setPriceCombinations] = useState<{ motiv: string; prelis: string }[]>([]);
  const [motivOrder, setMotivOrder] = useState<string[] | undefined>(undefined);
  const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null);

  useEffect(() => {
    const langParam = searchParams.get("lang")?.toLowerCase();
    const pathHasHu = location.pathname.includes("/hu/");
    const useHu = pathHasHu || langParam === "hu";
    i18n.changeLanguage(useHu ? "hu" : "sk");
  }, [location.pathname, searchParams, i18n]);

  useEffect(() => {
    fetch(apiUrl("/api/public/configurator/options"))
      .then((r) => r.json())
      .then((data) => {
        const arr = data.configuratorProductOptions;
        setOptions(Array.isArray(arr) ? arr : []);
        setPriceCombinations(Array.isArray(data.priceCombinations) ? data.priceCombinations : []);
        if (Array.isArray(data.motivOrder) && data.motivOrder.length > 0) {
          setMotivOrder(data.motivOrder);
        }
      })
      .catch(() => {});
  }, []);

  const handleSubmitToPublic = async (
    values: ConfiguratorFormValues,
    submitToken: string
  ): Promise<void> => {
    setSubmitResult(null);
    try {
      const res = await fetch(apiUrl("/api/public/configurator"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Configurator-Token": submitToken,
        },
        body: JSON.stringify({
          width: values.width,
          height: values.height,
          motiv: values.motiv,
          surface: values.surface,
          color: values.color || undefined,
          electricDrive: values.electricDrive,
          address: values.address || undefined,
          name: values.name,
          email: values.email,
          phone: values.phone || undefined,
          city: values.city || undefined,
          country: values.country || undefined,
          message: values.message || undefined,
          chcemNaFirmu: values.chcemNaFirmu === true,
          obchodneMeno: values.obchodneMeno?.trim() || undefined,
          ico: values.ico || undefined,
          dic: values.dic || undefined,
          icDph: values.icDph || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.dealId) {
        reportConfiguratorConversion(values);
        setSubmitResult({
          success: true,
          dealId: data.dealId,
          resultType: data.resultType ?? "custom_required",
          noMatchReason: data.noMatchReason ?? undefined,
          quoteSummary: {
            email: values.email,
            width: values.width,
            height: values.height,
            surface: values.surface || undefined,
            color: values.color || undefined,
            motiv: values.motiv || undefined,
            electricDrive: values.electricDrive,
          },
        });
      } else {
        // Handle both our API format ({ error, message }) and field-based format ({ email: ["..."] })
        const messageFromEmailArray = Array.isArray(data.email) ? data.email[0] : null;
        const errorMessage =
          data.error === "invalid_email"
            ? t("configurator.errorInvalidEmail")
            : data.error === "missing_params"
              ? (data.message || t("configurator.errorMissingParams"))
              : messageFromEmailArray ?? data.message ?? data.error ?? `HTTP ${res.status}`;
        setSubmitResult({
          success: false,
          error: errorMessage,
        });
      }
    } catch (err) {
      setSubmitResult({
        success: false,
        error: err instanceof Error ? err.message : "Request failed",
      });
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background text-foreground">
        <p className="text-muted-foreground">Missing token. Use ?token=... in the URL.</p>
      </div>
    );
  }

  // Az iframe magasságát töltsük ki: html/body/#root a global.css .embed-configurator-active alatt 100%
  useEffect(() => {
    document.documentElement.classList.add("embed-configurator-active");
    return () => {
      document.documentElement.classList.remove("embed-configurator-active");
    };
  }, []);

  if (submitResult) {
    return (
      <ResultScreen
        result={submitResult}
        onReset={() => setSubmitResult(null)}
      />
    );
  }

  return (
    <div className="h-full min-h-0 overflow-hidden flex flex-col bg-background text-foreground">
      <div className="flex-1 min-h-0 w-full mx-auto flex flex-col">
        <ConfiguratorLayout
          configuratorProductOptions={options}
          priceCombinations={priceCombinations}
          configuratorMotivOrder={motivOrder}
          embedMode={true}
          embedToken={token ?? undefined}
          onSubmitToPublic={handleSubmitToPublic}
        />
      </div>
    </div>
  );
}
